import React from "react";
import { useTranslation } from "react-i18next";
import { Store } from "lucide-react";
import { cn } from "@/lib/utils";
import { useProductDetail } from "../hooks/use-product-detail";

const formatPrice = (value: number | null, currency: string | null): string => {
  if (value == null) return "-";
  const formatted = value.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (!currency || currency === "TRY" || currency === "TL") {
    return `${formatted} TL`;
  }
  const symbols: Record<string, string> = { USD: "$", EUR: "€", GBP: "£" };
  const symbol = symbols[currency] || currency;
  return `${symbol}${formatted}`;
};

export const OtherSellersSection: React.FC = () => {
  const { t } = useTranslation();
  const { otherSellers, currentOfferId, isLoading, onSellerSelect } =
    useProductDetail();

  if (isLoading || !otherSellers || otherSellers.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <Store className="size-3.5 text-text-tertiary dark:text-text-tertiary flex-shrink-0" />
        <span className="text-[11px] font-semibold text-text-tertiary dark:text-text-tertiary uppercase tracking-wider">
          {t("productDetail.otherSellers")}
        </span>
        <span className="text-[10px] font-medium text-text-tertiary dark:text-text-tertiary bg-bg-secondary dark:bg-bg-tertiary border border-border-secondary dark:border-border-primary rounded-full px-2 py-px leading-none">
          {otherSellers.length}
        </span>
      </div>

      {/* Sellers Cards — horizontal scroll */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
        {otherSellers.map((item) => {
          const isCurrentOffer = item.offer.id === currentOfferId;
          const salePrice = item.offer.prices?.salePrice?.value;
          const listPrice = item.offer.prices?.listPrice?.value;
          const currency =
            item.offer.prices?.salePrice?.currency ||
            item.offer.prices?.listPrice?.currency;
          const effectivePrice = salePrice ?? listPrice;
          const hasDiscount =
            listPrice != null && salePrice != null && listPrice > salePrice;

          return (
            <button
              key={item.offer.id}
              onClick={() => onSellerSelect(item.offer.id)}
              className={cn(
                "flex-shrink-0 flex flex-col gap-2 px-3 py-2.5 rounded-lg border transition-all text-left w-[148px] hover:cursor-pointer",
                isCurrentOffer
                  ? "bg-bg-secondary dark:bg-bg-tertiary border-text-primary dark:border-white cursor-default"
                  : item.offer.inStock
                    ? "bg-white dark:bg-bg-secondary border-border-secondary dark:border-border-primary hover:border-text-primary dark:hover:border-white hover:bg-bg-secondary dark:hover:bg-bg-tertiary cursor-pointer"
                    : "bg-bg-secondary dark:bg-bg-tertiary border-border-secondary dark:border-border-primary opacity-40 cursor-not-allowed",
              )}
            >
              {/* Seller name */}
              <span
                className={cn(
                  "text-[12px] font-semibold leading-tight truncate block w-full",
                  isCurrentOffer
                    ? "text-text-primary dark:text-text-primary"
                    : "text-text-secondary dark:text-text-secondary",
                )}
              >
                {item.seller.name}
              </span>

              {/* Stock badge */}
              <div className="flex items-center">
                {item.offer.inStock ? (
                  <span className="text-[10px] font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-full px-2 py-0.5 leading-none whitespace-nowrap">
                    {t("productDetail.inStockShort")}
                    {item.offer.stockCount != null &&
                      item.offer.stockCount < 10 &&
                      ` (${item.offer.stockCount})`}
                  </span>
                ) : (
                  <span className="text-[10px] font-medium text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-full px-2 py-0.5 leading-none whitespace-nowrap">
                    {t("productDetail.outOfStock")}
                  </span>
                )}
              </div>

              {/* Price */}
              <div className="flex flex-col gap-0.5">
                <span
                  className={cn(
                    "text-[13px] font-bold leading-none",
                    isCurrentOffer
                      ? "text-text-primary dark:text-text-primary"
                      : "text-text-secondary dark:text-text-secondary",
                  )}
                >
                  {formatPrice(effectivePrice ?? null, currency ?? null)}
                </span>
                {hasDiscount && (
                  <span className="text-[11px] text-text-tertiary dark:text-text-tertiary line-through leading-none">
                    {formatPrice(listPrice ?? null, currency ?? null)}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
